import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // Add navigation
import api from "../utils/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import OnOffToggle from "./OnOffToggle";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Plus, Trash2, Package, LayoutGrid, List, Edit, ArrowUpDown, ArrowUp, ArrowDown, CheckSquare, Users, X, Upload, FileText, Image as ImageIcon, Download, Loader2, Settings } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import ConfirmDialog from "./ConfirmDialog";
import Pagination from "./Pagination";
import ImageUploadDropzone from "./ImageUploadDropzone";
import AddAssetModal from "./AddAssetModal";
import { GUIDE_CONTENT } from '../config/guideContent';
import DashboardPageLayout from './DashboardPageLayout';
import CardListViewToggle from './CardListViewToggle';
import MassActionToolbar from './MassActionToolbar';
import EmptyState from './EmptyState';
import { PrimaryButton } from './Buttons';
import ActionBar from './ActionBar';
import StandardModal from './StandardModal';
import InfoCard from './InfoCard';
import { useRoles, useAssets } from '../hooks/useData';
import { SecondaryButton, DangerButton } from './Buttons';
import TeamMemberSelector from './TeamMemberSelector';
import RoleSelector from './RoleSelector';
import ResourceCard from './ResourceCard';
import { BACKEND_URL, API_URL } from '../config/api';

const API = API_URL;

const ASSET_TYPES = [
  { value: "single-decker", label: "Single Decker", color: "bg-blue-500" },
  { value: "double-decker", label: "Double Decker", color: "bg-purple-500" },
  { value: "donut", label: "Donut", color: "bg-orange-500" }
];

const AssetsTab = ({ assets, teamMembers, onRefresh }) => {
  const navigate = useNavigate();
  const { roles } = useRoles(); // Use useSWR for roles
  const [open, setOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [viewMode, setViewMode] = useState("card"); // Default to card view
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: "" });
  const [editingAsset, setEditingAsset] = useState(null);
  const [restrictTeam, setRestrictTeam] = useState(false);

  // Helper function to generate multi-color left border based on team requirements
  const getAssetBorderColors = (asset) => {
    if (!asset.teamRequirements || asset.teamRequirements.length === 0) {
      // No team requirements = no border (visual indicator nothing required)
      return [];
    }

    // Get unique role colors from team requirements
    const roleColors = asset.teamRequirements
      .map(req => {
        const role = roles.find(r => r.id === req.roleId);
        return role?.color || '#3B82F6'; // Default to blue if role not found
      })
      .filter((color, index, self) => self.indexOf(color) === index); // Remove duplicates

    return roleColors;
  };
  const [showStepGuide, setShowStepGuide] = useState(() => {
    return localStorage.getItem('hideStepGuide') !== 'true';
  });

  // Listen for guide visibility changes
  useEffect(() => {
    const handleGuideChange = () => {
      setShowStepGuide(localStorage.getItem('hideStepGuide') !== 'true');
    };
    window.addEventListener('guideVisibilityChanged', handleGuideChange);
    return () => window.removeEventListener('guideVisibilityChanged', handleGuideChange);
  }, []);
  const [name, setName] = useState("");
  const [capacity, setCapacity] = useState("12");
  const [active, setActive] = useState(true);
  const [notes, setNotes] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [assetImages, setAssetImages] = useState([]);
  const [assetDocuments, setAssetDocuments] = useState([]); // NEW: Asset documents
  const [saving, setSaving] = useState(false); // Loading state for save button
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [allowedTeamMembers, setAllowedTeamMembers] = useState([]);
  const [teamRequirements, setTeamRequirements] = useState([]); // NEW: [{roleId, roleName, quantity}]
  
  // Remove old manual roles fetch - now using useRoles hook
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  
  // Mass action states
  const [massActionMode, setMassActionMode] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [expandedAssets, setExpandedAssets] = useState({});

  // Removed manual roles fetching - now using useRoles hook

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 inline" />;
    return sortDirection === "asc" ? 
      <ArrowUp className="w-3 h-3 ml-1 inline" /> : 
      <ArrowDown className="w-3 h-3 ml-1 inline" />;
  };
  
  // Mass action handlers
  const toggleMassActionMode = () => {
    setMassActionMode(!massActionMode);
    setSelectedAssets([]);
  };
  
  const toggleAssetSelection = (assetId) => {
    setSelectedAssets(prev => 
      prev.includes(assetId) 
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };
  
  const selectAllAssets = () => {
    setSelectedAssets(paginatedAssets.map(asset => asset.id));
  };
  
  const deselectAllAssets = () => {
    setSelectedAssets([]);
  };
  
  const handleBulkDelete = async () => {
    try {
      const deletePromises = selectedAssets.map(assetId =>
        api.delete(`/assets/${assetId}`)
      );
      
      await Promise.all(deletePromises);
      toast.success(`${selectedAssets.length} asset(s) deleted successfully`);
      setSelectedAssets([]);
      setMassActionMode(false);
      setBulkDeleteConfirm(false);
      onRefresh();
    } catch (error) {
      console.error("Error deleting assets:", error);
      toast.error("Failed to delete some assets. They may be assigned to trips.");
      setBulkDeleteConfirm(false);
    }
  };


  const handleEditClick = (asset) => {
    setEditingAsset(asset);
    setName(asset.name);
    setCapacity(asset.capacity?.toString() || "12");
    setActive(asset.active !== undefined ? asset.active : true);
    setNotes(asset.notes || "");
    setPhotoUrl(asset.photoUrl || "");
    setAssetImages(asset.photos && asset.photos.length > 0 
      ? asset.photos 
      : (asset.photoUrl && asset.photoUrl.startsWith('http')) 
        ? [asset.photoUrl] 
        : []);
    setAssetDocuments(asset.documents || []);
    
    // Load from allowedTeam field (new) or fallback to allowedTeamMembers (old)
    const allowedTeamList = asset.allowedTeam || asset.allowedTeamMembers || [];
    setAllowedTeamMembers(allowedTeamList);
    setRestrictTeam(allowedTeamList.length > 0);
    
    setTeamRequirements(asset.teamRequirements || []);
    setOpen(true);
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Check limit
    if (assetImages.length + files.length > 5) {
      toast.error("Maximum 5 photos allowed per asset");
      return;
    }
    
    setUploadingPhoto(true);
    try {
      const uploadedUrls = [];
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        console.log('Uploading photo:', file.name, file.type, file.size);
        
        const response = await api.post('/upload/asset-photo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        console.log('Photo upload response:', response.data);
        uploadedUrls.push(response.data.url);
      }
      
      setAssetImages(prev => [...prev, ...uploadedUrls]);
      toast.success(`${files.length} photo(s) uploaded`);
    } catch (error) {
      console.error("Error uploading photos:", error);
      console.error("Error details:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };
  
  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Check limit
    if (assetDocuments.length + files.length > 10) {
      toast.error("Maximum 10 documents allowed per asset");
      return;
    }
    
    setUploadingDocument(true);
    try {
      const uploadedDocs = [];
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        console.log('Uploading document:', file.name, file.type, file.size);
        
        const response = await api.post('/upload/asset-document', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        console.log('Document upload response:', response.data);
        
        uploadedDocs.push({
          filename: file.name,
          url: response.data.url,
          uploadedAt: new Date().toISOString(),
          size: file.size
        });
      }
      
      setAssetDocuments(prev => [...prev, ...uploadedDocs]);
      toast.success(`${files.length} document(s) uploaded`);
    } catch (error) {
      console.error("Error uploading documents:", error);
      console.error("Error details:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploadingDocument(false);
    }
  };
  
  const handleRemovePhoto = (index) => {
    setAssetImages(prev => prev.filter((_, i) => i !== index));
    toast.success("Photo removed");
  };
  
  const handleRemoveDocument = (index) => {
    setAssetDocuments(prev => prev.filter((_, i) => i !== index));
    toast.success("Document removed");
  };

  const handleSubmit = async () => {
    if (!name) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Check for duplicate asset name
    const duplicateAsset = assets.find(a => 
      a.name.toLowerCase().trim() === name.toLowerCase().trim() && 
      a.id !== editingAsset?.id
    );
    
    if (duplicateAsset) {
      toast.error(`An asset named "${name}" already exists`);
      return;
    }

    setSaving(true);
    try {
      const payload = { 
        name: name.trim(), 
        capacity: parseInt(capacity) || 12, // Use actual capacity value
        active, 
        notes: notes?.trim() || null,
        photoUrl: assetImages.length > 0 ? assetImages[0] : null,
        photos: assetImages, // NEW: Array of all asset photos
        documents: assetDocuments, // NEW: Array of asset documents
        allowedTeam: restrictTeam ? allowedTeamMembers : [], // FIXED: Use allowedTeam field
        teamRequirements: teamRequirements
      };
      
      console.log('Asset payload being sent:', JSON.stringify(payload, null, 2));
      
      if (editingAsset) {
        await api.put(`/assets/${editingAsset.id}`, payload);
        toast.success("Asset updated successfully");
      } else {
        await api.post(`/assets`, payload);
        toast.success("Asset added successfully");
      }
      setOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      console.error("Error saving asset:", error);
      toast.error("Failed to save asset");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (asset) => {
    setDeleteConfirm({ open: true, id: asset.id, name: asset.name });
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/assets/${deleteConfirm.id}`);
      toast.success("Asset deleted successfully");
      setDeleteConfirm({ open: false, id: null, name: "" });
      onRefresh();
    } catch (error) {
      console.error("Error deleting asset:", error);
      const errorMsg = error.response?.data?.detail || "Failed to delete asset";
      
      // Show detailed error message for referential integrity violations
      if (error.response?.status === 400) {
        toast.error(errorMsg, { duration: 6000 });
      } else {
        toast.error("Failed to delete asset");
      }
      
      setDeleteConfirm({ open: false, id: null, name: "" });
    }
  };

  const resetForm = () => {
    setEditingAsset(null);
    setName("");
    setCapacity("12");
    setActive(true);
    setNotes("");
    setPhotoUrl("");
    setAssetImages([]);
    setAllowedTeamMembers([]);
    setRestrictTeam(false); // Reset restriction mode
    setTeamRequirements([]); // Reset team requirements
  };

  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (!isOpen) {
      resetForm();
    }
  };

  const getAssetTypeInfo = (typeValue) => {
    return ASSET_TYPES.find(bt => bt.value === typeValue) || ASSET_TYPES[0];
  };

  // Sort assets
  const sortedAssets = [...assets].sort((a, b) => {
    let compareValue = 0;
    
    switch (sortField) {
      case "name":
        compareValue = a.name.localeCompare(b.name);
        break;
      case "type":
        compareValue = a.type.localeCompare(b.type);
        break;
      default:
        compareValue = 0;
    }
    
    return sortDirection === "asc" ? compareValue : -compareValue;
  });

  // Pagination
  const totalPages = Math.ceil(sortedAssets.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAssets = sortedAssets.slice(startIndex, startIndex + itemsPerPage);

  return (
    <DashboardPageLayout
      title="Assets"
      subtitle="Register your resources"
      guideChildren={
        showStepGuide && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 w-full">
            <div className="flex-1">
              <div className="flex items-start sm:items-center gap-3">
                {assets.length > 0 ? (
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    ✓
                  </div>
                ) : (
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    3
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900">
                    {GUIDE_CONTENT.assets.title}
                  </h3>
                  <p className="text-sm text-gray-700 mt-1">
                    {GUIDE_CONTENT.assets.dynamicDescription(assets.length)}
                  </p>
                </div>
              </div>
            </div>
            <Button 
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowStepGuide(false);
                localStorage.setItem('hideStepGuide', 'true');
                window.dispatchEvent(new Event('guideVisibilityChanged'));
              }}
              className="text-gray-500 hover:text-gray-700 flex-shrink-0"
            >
              <X className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Dismiss</span>
            </Button>
          </div>
        )
      }
      actions={
        <div className="flex items-center gap-2 w-full">
          {/* Mass Actions - Icon only */}
          <Button
            onClick={toggleMassActionMode}
            variant="outline"
            size="sm"
            className={`h-9 w-9 p-0 flex items-center justify-center ${
              massActionMode ? 'bg-blue-50 border-blue-500 text-blue-600' : ''
            }`}
          >
            <CheckSquare className="w-4 h-4" />
          </Button>
          
          <CardListViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />
          
          <PrimaryButton 
            onClick={() => setShowAddModal(true)}
            className="h-9 flex-1 sm:flex-none sm:px-4"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add
          </PrimaryButton>
        </div>
      }
    >
      <div data-testid="assets-tab" className="space-y-6">
      <AddAssetModal 
        isOpen={showAddModal} 
        onClose={() => setShowAddModal(false)} 
        onSuccess={onRefresh}
        teamMembers={teamMembers}
      />
      
      {/* Edit Asset Modal */}
      {editingAsset && (
        <StandardModal
          isOpen={open}
          onClose={handleOpenChange}
          title="Edit Asset"
          description={`Update ${editingAsset.name}'s details`}
          size="md"
          footer={
            <>
              <SecondaryButton onClick={() => handleOpenChange(false)} className="flex-1 sm:flex-none">
                Cancel
              </SecondaryButton>
              <PrimaryButton onClick={handleSubmit} disabled={saving} className="flex-1 sm:flex-none">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </PrimaryButton>
            </>
          }
        >
          <Tabs defaultValue="basic" className="w-full">
            <div className="mb-4">
              <TabsList className="grid w-full grid-cols-4 h-auto sm:h-11">
                <TabsTrigger value="basic" className="text-xs sm:text-sm py-2">Basic</TabsTrigger>
                <TabsTrigger value="team" className="text-xs sm:text-sm py-2">Team</TabsTrigger>
                <TabsTrigger value="photos" className="text-xs sm:text-sm py-2">Photos</TabsTrigger>
                <TabsTrigger value="documents" className="text-xs sm:text-sm py-2">Docs</TabsTrigger>
              </TabsList>
            </div>
            
            {/* Tab 1: Basic Info */}
            <TabsContent value="basic" className="space-y-4">
              <div>
                <Label htmlFor="asset-name">Asset Name *</Label>
                <Input
                  id="asset-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Sea Breeze"
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="asset-capacity">Capacity (passengers) *</Label>
                  <Input
                    id="asset-capacity"
                    type="number"
                    value={capacity}
                    onChange={(e) => setCapacity(e.target.value)}
                    placeholder="12"
                    className="mt-1"
                  />
                </div>
                <div className="flex items-center justify-between gap-2 mt-7">
                  <Label>Active {!active && <span className="text-red-600">(not assigned to trips)</span>}</Label>
                  <OnOffToggle
                    value={active}
                    onChange={setActive}
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="asset-notes">Notes (Optional)</Label>
                <Textarea
                  id="asset-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g., Under maintenance until Dec 15th"
                  className="mt-1"
                  rows={3}
                />
              </div>
            </TabsContent>
            
            {/* Tab 2: Team Requirements */}
            <TabsContent value="team" className="space-y-4">
              <div>
                <Label className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4" />
                  Team Requirements
                </Label>
                <div className="space-y-3">
                  {teamRequirements.map((requirement, index) => (
                    <div key={index} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <RoleSelector
                        roles={roles}
                        value={requirement.roleId}
                        onChange={(roleId) => {
                          const role = roles.find(r => r.id === roleId);
                          const updated = [...teamRequirements];
                          updated[index] = {
                            ...requirement,
                            roleId,
                            roleName: role?.name || ''
                          };
                          setTeamRequirements(updated);
                        }}
                        placeholder="Select role..."
                        className="flex-1"
                      />
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="1"
                          max="10"
                          value={requirement.quantity}
                          onChange={(e) => {
                            const updated = [...teamRequirements];
                            updated[index].quantity = parseInt(e.target.value) || 1;
                            setTeamRequirements(updated);
                          }}
                          className="w-20"
                          placeholder="Qty"
                        />
                        <SecondaryButton
                          size="sm"
                          onClick={() => {
                            const updated = teamRequirements.filter((_, i) => i !== index);
                            setTeamRequirements(updated);
                          }}
                          className="p-2"
                        >
                          <Trash2 className="w-3 h-3" />
                        </SecondaryButton>
                      </div>
                    </div>
                  ))}
                  
                  <SecondaryButton
                    onClick={() => {
                      setTeamRequirements([...teamRequirements, {
                        roleId: '',
                        roleName: '',
                        quantity: 1
                      }]);
                    }}
                    className="w-full"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Team Role
                  </SecondaryButton>
                </div>
              </div>

              {/* Allowed Team */}
              <div>
                <Label className="mb-3 block">
                  Allowed TeamMembers 
                  <span className="text-xs text-gray-500 ml-1">(leave empty to allow all)</span>
                </Label>
                
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mb-3">
                  <p className="text-sm text-blue-800">
                    Certain assets may require specific experience levels. You can limit access to experienced teamMembers.
                  </p>
                </div>
                
                {!restrictTeam ? (
                  <div className="text-center py-4 bg-gray-50 rounded-lg border border-dashed">
                    <p className="text-sm text-gray-500">All teamMembers can use this asset</p>
                    <SecondaryButton
                      onClick={() => setRestrictTeam(true)}
                      size="sm"
                      className="mt-2"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Restrict to Specific Team
                    </SecondaryButton>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-1 p-3 bg-gray-50 rounded-lg min-h-[40px]">
                      {allowedTeamMembers.length === 0 ? (
                        <p className="text-xs text-gray-500 italic">No team selected - use dropdown below to add teamMembers</p>
                      ) : (
                        allowedTeamMembers.map(teamMemberId => {
                          const teamMember = teamMembers.find(c => c.id === teamMemberId);
                          return teamMember ? (
                            <div key={teamMemberId} className="flex items-center gap-1 bg-white rounded px-2 py-1">
                              <span className="text-xs font-medium">{teamMember.name}</span>
                              <button
                                onClick={() => {
                                  setAllowedTeamMembers(allowedTeamMembers.filter(id => id !== teamMemberId));
                                }}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : null;
                        })
                      )}
                    </div>
                    
                    <TeamMemberSelector
                      teamMembers={teamMembers.filter(c => !allowedTeamMembers.includes(c.id))}
                      value=""
                      onChange={(teamMemberId) => {
                        console.log('TeamMemberSelector onChange:', teamMemberId);
                        if (teamMemberId && teamMemberId !== "none" && teamMemberId !== "__add_new__") {
                          console.log('Adding teamMember to allowed list:', teamMemberId);
                          setAllowedTeamMembers([...allowedTeamMembers, teamMemberId]);
                        }
                      }}
                      placeholder="Add another teamMember..."
                      showUnassign={false}
                    />
                    
                    <div className="flex gap-2">
                      <SecondaryButton
                        onClick={() => {
                          setAllowedTeamMembers([]);
                          setRestrictTeam(false);
                        }}
                        size="sm"
                        className="text-xs"
                      >
                        Allow All TeamMembers
                      </SecondaryButton>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
            
            {/* Tab 3: Asset Photos */}
            <TabsContent value="photos" className="space-y-4">
              <div>
                <Label className="flex items-center gap-2 mb-3">
                  <ImageIcon className="w-4 h-4" />
                  Asset Photos ({assetImages.length}/5)
                </Label>
                
                {/* Photo Grid */}
                {assetImages.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                    {assetImages.map((url, index) => (
                      <div key={index} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200">
                        <img 
                          src={url} 
                          alt={`Asset ${index + 1}`} 
                          className="w-full h-full object-cover"
                        />
                        <button
                          onClick={() => handleRemovePhoto(index)}
                          className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload Button */}
                {assetImages.length < 5 && (
                  <div>
                    <input
                      type="file"
                      id="asset-photos"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      disabled={uploadingPhoto}
                      onClick={() => document.getElementById('asset-photos').click()}
                    >
                      {uploadingPhoto ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Photos ({5 - assetImages.length} remaining)
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-gray-500 mt-1">
                      Accepted: JPG, PNG, WEBP • Max 5 photos
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>
            
            {/* Tab 4: Document Photos */}
            <TabsContent value="documents" className="space-y-4">
              <div>
                <Label className="flex items-center gap-2 mb-3">
                  <FileText className="w-4 h-4" />
                  Document Photos ({assetDocuments.length}/10)
                </Label>
                <p className="text-xs text-gray-600 mb-3">Photos of insurance, registration, certificates</p>
                
                {/* Documents Grid (same as photos) */}
                {assetDocuments.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                    {assetDocuments.map((doc, index) => (
                      <div key={index} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200">
                        <img 
                          src={doc.url} 
                          alt={doc.filename}
                          className="w-full h-full object-cover cursor-pointer"
                          onClick={() => window.open(doc.url, '_blank')}
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs p-1.5 truncate">
                          {doc.filename}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveDocument(index);
                          }}
                          className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload Button */}
                {assetDocuments.length < 10 && (
                  <div>
                    <input
                      type="file"
                      id="asset-documents"
                      accept="image/*"
                      multiple
                      onChange={handleDocumentUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      disabled={uploadingDocument}
                      onClick={() => document.getElementById('asset-documents').click()}
                    >
                      {uploadingDocument ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Document Photos ({10 - assetDocuments.length} remaining)
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-gray-500 mt-1">
                      Take photos of insurance, registration, certificates
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </StandardModal>
      )}
      
      {/* Mass Action Toolbar */}
      {massActionMode && (
        <MassActionToolbar
          selectedCount={selectedAssets.length}
          onSelectAll={selectAllAssets}
          onDeselectAll={deselectAllAssets}
          onDelete={() => setBulkDeleteConfirm(true)}
          itemName="asset"
        />
      )}

      {!assets ? (
        <EmptyState
          icon={Package}
          title="Loading assets..."
          showAction={false}
        />
      ) : assets.length === 0 ? (
        <EmptyState
          icon={Settings}
          title="No Assets Added Yet"
          description="Click 'Add New Asset' to register your first asset"
          actionLabel="Add New Asset"
          onAction={() => setShowAddModal(true)}
        />
      ) : viewMode === "card" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paginatedAssets.map((asset) => {
            const typeInfo = getAssetTypeInfo(asset.type);
            const isSelected = selectedAssets.includes(asset.id);
            const borderColors = getAssetBorderColors(asset);
            
            // Icon color: use first team requirement role color, or default based on active status
            let iconColor = asset.active ? '#10B981' : '#6B7280';
            if (asset.teamRequirements && asset.teamRequirements.length > 0) {
              const firstRole = roles.find(r => r.id === asset.teamRequirements[0].roleId);
              if (firstRole) iconColor = firstRole.color;
            }
            
            // Build expanded content JSX
            const expandedContent = (
              <>
                {/* Team Requirements */}
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    Team Required
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {asset.teamRequirements && asset.teamRequirements.length > 0 ? (
                      asset.teamRequirements.map((requirement, index) => (
                        <Badge 
                          key={index}
                          className="text-xs"
                          style={{ 
                            backgroundColor: roles.find(r => r.id === requirement.roleId)?.color || '#3B82F6',
                            color: 'white'
                          }}
                        >
                          {requirement.quantity} {requirement.roleName}
                        </Badge>
                      ))
                    ) : (
                      <Badge variant="outline" className="text-xs text-gray-500">
                        Self-service (no team required)
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Allowed Team */}
                {(asset.allowedTeam || asset.allowedTeamMembers) && (asset.allowedTeam || asset.allowedTeamMembers).length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">Allowed Team ({(asset.allowedTeam || asset.allowedTeamMembers).length})</p>
                    <div className="flex flex-wrap gap-1">
                      {(asset.allowedTeam || asset.allowedTeamMembers).slice(0, 3).map(teamMemberId => {
                        const teamMember = teamMembers.find(c => c.id === teamMemberId);
                        return teamMember ? (
                          <Badge key={teamMemberId} variant="secondary" className="text-xs">
                            {teamMember.name}
                          </Badge>
                        ) : null;
                      })}
                      {(asset.allowedTeam || asset.allowedTeamMembers).length > 3 && (
                        <span className="text-xs text-gray-500">+{(asset.allowedTeam || asset.allowedTeamMembers).length - 3} more</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {asset.notes && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">Notes</p>
                    <div className="p-2 bg-gray-50 rounded text-xs text-gray-600 italic">
                      {asset.notes}
                    </div>
                  </div>
                )}
                
                {/* Asset Photos */}
                {asset.photos && asset.photos.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                      <ImageIcon className="w-4 h-4" />
                      Photos ({asset.photos.length})
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {asset.photos.map((url, idx) => (
                        <img 
                          key={idx} 
                          src={url} 
                          alt={`${asset.name} ${idx + 1}`}
                          className="w-full aspect-square object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-75 transition"
                          onClick={() => window.open(url, '_blank')}
                        />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Document Photos (Photos of Paperwork) */}
                {asset.documents && asset.documents.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      Document Photos ({asset.documents.length})
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {asset.documents.map((doc, idx) => (
                        <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200">
                          <img 
                            src={doc.url} 
                            alt={doc.filename}
                            className="w-full h-full object-cover cursor-pointer hover:opacity-75 transition"
                            onClick={() => window.open(doc.url, '_blank')}
                          />
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs p-1 truncate">
                            {doc.filename}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
            
            return (
              <ResourceCard
                key={asset.id}
                id={asset.id}
                name={asset.name}
                icon={Package}
                iconColor={iconColor}
                primaryBadge={{
                  label: asset.active ? 'Active' : 'Inactive',
                  variant: 'outline',
                  className: `text-xs mb-2 ${asset.active ? 'border-green-500 text-green-700' : 'border-gray-500 text-gray-700'}`
                }}
                details={[
                  { icon: <Users className="w-3 h-3" />, text: `${asset.capacity || 12} passengers` }
                ]}
                showSeeMore={true}
                expandedContent={expandedContent}
                isExpanded={expandedAssets[asset.id]}
                onToggleExpand={() => {
                  const allExpanded = Object.keys(expandedAssets).length === paginatedAssets.length && 
                                     Object.values(expandedAssets).every(val => val);
                  
                  if (allExpanded) {
                    setExpandedAssets({});
                  } else {
                    const newExpanded = {};
                    paginatedAssets.forEach(v => {
                      newExpanded[v.id] = true;
                    });
                    setExpandedAssets(newExpanded);
                  }
                }}
                leftBorderColors={borderColors}
                massActionMode={massActionMode}
                isSelected={isSelected}
                onToggleSelect={() => toggleAssetSelection(asset.id)}
                additionalClassNames={!asset.active ? 'opacity-75' : ''}
                onEdit={() => handleEditClick(asset)}
                onDelete={() => handleDeleteClick(asset)}
              />
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
          <table className="w-full min-w-[768px]">
            <thead className="bg-gray-50 border-b">
              <tr>
                {massActionMode && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedAssets.length === paginatedAssets.length && paginatedAssets.length > 0}
                      onChange={(e) => e.target.checked ? selectAllAssets() : deselectAllAssets()}
                      className="w-4 h-4 cursor-pointer"
                    />
                  </th>
                )}
                <th 
                  onClick={() => handleSort("name")}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  Name {getSortIcon("name")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Allowed Team
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    Team Requirements
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedAssets.map((asset) => {
                const typeInfo = getAssetTypeInfo(asset.type);
                const isSelected = selectedAssets.includes(asset.id);
                return (
                  <tr key={asset.id} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                    {massActionMode && (
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleAssetSelection(asset.id)}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Package className="w-5 h-5 text-cyan-600 mr-2" />
                        <span className="font-medium text-gray-900">{asset.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {(asset.allowedTeam || asset.allowedTeamMembers) && (asset.allowedTeam || asset.allowedTeamMembers).length > 0 ? (
                          (asset.allowedTeam || asset.allowedTeamMembers).map(teamMemberId => {
                            const teamMember = teamMembers.find(c => c.id === teamMemberId);
                            return teamMember ? (
                              <Badge key={teamMemberId} variant="secondary" className="text-xs">
                                {teamMember.name}
                              </Badge>
                            ) : null;
                          })
                        ) : (
                          <span className="text-sm text-gray-400">All teamMembers allowed</span>
                        )}
                      </div>
                    </td>
                    
                    {/* Team Requirements Cell */}
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {asset.teamRequirements && asset.teamRequirements.length > 0 ? (
                          asset.teamRequirements.map((requirement, index) => (
                            <Badge 
                              key={index}
                              className="text-xs"
                              style={{ 
                                backgroundColor: roles.find(r => r.id === requirement.roleId)?.color || '#3B82F6',
                                color: 'white'
                              }}
                            >
                              {requirement.quantity} {requirement.roleName}
                            </Badge>
                          ))
                        ) : (
                          <Badge variant="outline" className="text-xs text-gray-500">
                            ⭐ No team required
                          </Badge>
                        )}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <Button
                          data-testid={`edit-asset-list-${asset.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditClick(asset)}
                          className="text-cyan-600 hover:text-cyan-800 hover:bg-cyan-50"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`delete-asset-list-${asset.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(asset)}
                          className="text-red-600 hover:text-red-800 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {sortedAssets.length > itemsPerPage && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          totalItems={sortedAssets.length}
        />
      )}

      {/* Delete Asset Modal */}
      <StandardModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null, name: "" })}
        title="Delete Asset?"
        description={`Are you sure you want to delete ${deleteConfirm.name}?`}
        size="sm"
        footer={
          <>
            <SecondaryButton
              onClick={() => setDeleteConfirm({ open: false, id: null, name: "" })}
              className="flex-1"
            >
              Cancel
            </SecondaryButton>
            <DangerButton
              onClick={handleDeleteConfirm}
              className="flex-1"
            >
              Delete Asset
            </DangerButton>
          </>
        }
      >
        <InfoCard variant="danger">
          <p className="font-medium mb-1">This action cannot be undone.</p>
          <p>This asset will be unassigned from all future trips.</p>
        </InfoCard>
      </StandardModal>
      
      {/* Bulk Delete Modal */}
      <StandardModal
        isOpen={bulkDeleteConfirm}
        onClose={() => setBulkDeleteConfirm(false)}
        title="Delete Multiple Assets?"
        description={`Are you sure you want to delete ${selectedAssets.length} asset(s)?`}
        size="sm"
        footer={
          <>
            <SecondaryButton
              onClick={() => setBulkDeleteConfirm(false)}
              className="flex-1"
            >
              Cancel
            </SecondaryButton>
            <DangerButton
              onClick={handleBulkDelete}
              className="flex-1"
            >
              Delete {selectedAssets.length} Assets
            </DangerButton>
          </>
        }
      >
        <InfoCard variant="danger">
          <p className="font-medium mb-1">This action cannot be undone.</p>
          <p>Assets assigned to trips may fail to delete.</p>
        </InfoCard>
      </StandardModal>
      </div>
    </DashboardPageLayout>
  );
};

export default AssetsTab;